import React, { useState, useEffect } from 'react';

/**
 * ResultsDisplay — CoThink Compass
 * Shows assessment results: overall score, fluency level, breakdown across
 * the 5Cs, and report delivery status.
 *
 * VISUAL RESTYLE ONLY: score/bar animations, the 5-second report timer, the
 * competencyNames key mapping ('1'-'5'), and the results prop shape are
 * unchanged. Level 3 remains "AI Literate" (canonical; "AI Proficient" is
 * retired). The rainbow level gradients are retired in favor of the V5
 * navy/emerald system; AI Fluent carries the emerald emphasis, matching
 * the Ladder treatment on AIFluencyLevelsPage.
 */
const ResultsDisplay = ({ results }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [competencyAnimations, setCompetencyAnimations] = useState({});
  const [reportStatus, setReportStatus] = useState('generating'); // generating, ready, timeout

  const { total_score, fluency_level, competency_scores } = results;

  // Capability names mapping — aligned to the 5Cs (canonical order)
  const competencyNames = {
    '1': 'Conceptual Clarity',
    '2': 'Critical Evaluation',
    '3': 'Constructive Collaboration',
    '4': 'Creative Exploration',
    '5': 'Cognitive Agility'
  };

  // Fluency level styling and messages — aligned to the AI Fluency Ladder.
  // AI Fluent is the goal state and carries the emerald emphasis.
  const levelConfig = {
    'AI Aware': {
      scoreText: 'text-navy-700 dark:text-paper-100',
      badgeBg: 'bg-paper-300 dark:bg-navy-700',
      badgeText: 'text-navy-700 dark:text-paper-200',
      bar: 'bg-navy-400',
      message: "You're at the starting line. Begin building foundational AI knowledge and explore how AI tools can support your thinking."
    },
    'AI Curious': {
      scoreText: 'text-navy-700 dark:text-paper-100',
      badgeBg: 'bg-paper-300 dark:bg-navy-700',
      badgeText: 'text-navy-700 dark:text-paper-200',
      bar: 'bg-navy-400',
      message: "You're engaged and exploring. Deepen your understanding of AI capabilities and start developing critical evaluation habits."
    },
    'AI Literate': {
      scoreText: 'text-navy-800 dark:text-paper-100',
      badgeBg: 'bg-navy-100 dark:bg-navy-700',
      badgeText: 'text-navy-800 dark:text-paper-100',
      bar: 'bg-navy-500',
      message: 'You have solid working knowledge. Focus on applying AI strategically and strengthening your weakest capability areas.'
    },
    'AI Functional': {
      scoreText: 'text-emerald-700 dark:text-emerald-300',
      badgeBg: 'bg-emerald-50 dark:bg-emerald-900',
      badgeText: 'text-emerald-800 dark:text-emerald-200',
      bar: 'bg-emerald-500',
      message: 'You collaborate with AI at a high level. Refine your judgment, lead others, and prepare for organizational AI leadership.'
    },
    'AI Fluent': {
      scoreText: 'text-emerald-600 dark:text-emerald-300',
      badgeBg: 'bg-emerald-600',
      badgeText: 'text-white',
      bar: 'bg-emerald-500',
      message: 'Exceptional. You have achieved mastery across the 5Cs. Scale AI Fluency across your organization.'
    }
  };

  const currentLevel = levelConfig[fluency_level] || levelConfig['AI Curious'];

  // Wait for report generation (reports typically take ~5 seconds)
  useEffect(() => {
    // Since reports generate in ~5-10 seconds, we'll wait and then mark as ready
    const timer = setTimeout(() => {
      setReportStatus('ready');
    }, 5000); // Wait 5 seconds for report generation

    return () => clearTimeout(timer);
  }, []);

  // Animate score count-up
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = total_score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= total_score) {
        setAnimatedScore(total_score);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [total_score]);

  // Animate competency bars
  useEffect(() => {
    const timer = setTimeout(() => {
      const animations = {};
      Object.keys(competency_scores).forEach(key => {
        animations[key] = competency_scores[key];
      });
      setCompetencyAnimations(animations);
    }, 500);

    return () => clearTimeout(timer);
  }, [competency_scores]);

  const handleEmailClick = () => {
    alert('Please check your email inbox (and spam folder) for your CoThink Compass report.');
  };

  return (
    <div className="min-h-screen bg-navy-50 dark:bg-navy-900 pt-10 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-900 mb-6">
            <svg className="w-12 h-12 text-emerald-600 dark:text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="font-display text-display-md text-navy-900 dark:text-paper-100 mb-4">
            Assessment Complete!
          </h1>
          <p className="text-xl text-navy-600 dark:text-paper-300">
            Your personalized AI Fluency report is ready
          </p>
        </div>

        {/* Overall Score Card */}
        <div className="bg-paper-50 dark:bg-navy-800 rounded-2xl border border-paper-400 dark:border-navy-700 shadow-sm p-8 mb-8 text-center">
          <h2 className="font-display text-2xl font-bold text-navy-900 dark:text-paper-100 mb-6">Your AI Fluency Score</h2>

          <div className="mb-8">
            <div className="inline-flex items-baseline justify-center">
              <span className={`font-display text-7xl font-bold ${currentLevel.scoreText}`}>{animatedScore}</span>
              <span className="text-4xl font-bold text-navy-300 dark:text-paper-400 ml-2">/50</span>
            </div>
          </div>

          {/* Fluency Level Badge */}
          <div className={`inline-block px-8 py-4 rounded-full ${currentLevel.badgeBg} mb-6`}>
            <span className={`font-display text-2xl font-bold ${currentLevel.badgeText}`}>
              {fluency_level}
            </span>
          </div>

          <p className="text-lg text-navy-700 dark:text-paper-300 max-w-2xl mx-auto leading-relaxed">
            {currentLevel.message}
          </p>
        </div>

        {/* Breakdown across the 5Cs */}
        <div className="bg-paper-50 dark:bg-navy-800 rounded-2xl border border-paper-400 dark:border-navy-700 shadow-sm p-8 mb-8">
          <h3 className="font-display text-2xl font-bold text-navy-900 dark:text-paper-100 mb-8">Breakdown Across the 5Cs</h3>

          <div className="space-y-6">
            {Object.entries(competency_scores).map(([key, score]) => {
              const maxScore = 10;
              const percentage = (competencyAnimations[key] || 0) / maxScore * 100;

              return (
                <div key={key}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-navy-700 dark:text-paper-200">
                      {competencyNames[key]}
                    </span>
                    <span className="text-lg font-bold text-navy-900 dark:text-paper-100">
                      {score}/{maxScore}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-paper-300 dark:bg-navy-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${currentLevel.bar} rounded-full transition-all duration-1000 ease-out`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Report Status Card */}
        <div className="bg-navy-900 dark:bg-navy-950 border border-navy-700 rounded-2xl shadow-lg p-8 text-paper-100 text-center mb-8">
          {reportStatus === 'generating' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-paper-100/10 mb-4">
                <svg className="animate-spin h-10 w-10 text-emerald-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="font-display text-2xl font-bold mb-4">Generating Your Report...</h3>
              <p className="text-paper-300 text-lg mb-2">
                We're creating your personalized AI Fluency report with detailed insights.
              </p>
              <p className="text-navy-300 text-interface">
                This usually takes just a few seconds...
              </p>
            </>
          )}

          {reportStatus === 'ready' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-paper-100/10 mb-4">
                <svg className="w-10 h-10 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-display text-2xl font-bold mb-4">Check Your Email!</h3>
              <p className="text-paper-300 mb-2 text-lg">
                Your personalized AI Fluency report has been sent to your email address.
              </p>
              <p className="text-navy-300 text-interface mb-6">
                The report includes detailed insights, capability analysis, and personalized recommendations.
              </p>

              <button
                onClick={handleEmailClick}
                className="bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-500 transition-colors duration-300 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Open Your Email
              </button>

              <p className="text-interface text-paper-300 mt-6">
                Your full report link is in your email. Save it, revisit it, share it with your coach.
              </p>
            </>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-paper-50 dark:bg-navy-800 rounded-2xl border border-paper-400 dark:border-navy-700 shadow-sm p-8">
          <h3 className="font-sans text-[20px] font-semibold text-navy-900 dark:text-paper-100 mb-6">What next</h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-paper-400 dark:border-navy-700 rounded-xl p-6 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors duration-200">
              <h4 className="font-sans font-semibold text-lg text-navy-900 dark:text-paper-100 mb-2">The book: Thinking with AI</h4>
              <p className="text-navy-600 dark:text-paper-300 mb-4">
                The discipline behind the 5Cs, the Mastery Loop, and the Ladder, written for leaders. Out now in hardcover, paperback, and Kindle.
              </p>
              <a href="/book" className="text-emerald-700 dark:text-emerald-300 font-medium hover:text-emerald-800 dark:hover:text-emerald-200 inline-flex items-center min-h-tap">
                About the book
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </a>
            </div>

            <div className="border border-paper-400 dark:border-navy-700 rounded-xl p-6 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors duration-200">
              <h4 className="font-sans font-semibold text-lg text-navy-900 dark:text-paper-100 mb-2">Build the practice</h4>
              <p className="text-navy-600 dark:text-paper-300 mb-4">
                Advisory, executive education at OSU Fisher, or the Apprenticeship for your team. One conversation to find the right way in.
              </p>
              <a href="/what-we-do" className="text-emerald-700 dark:text-emerald-300 font-medium hover:text-emerald-800 dark:hover:text-emerald-200 inline-flex items-center min-h-tap">
                What we do
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </a>
            </div>
          </div>
        </div>

        {/* Return Home */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-navy-600 dark:text-paper-300 hover:text-navy-900 dark:hover:text-paper-100 font-medium inline-flex items-center transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Return to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;
